import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { EllipsisTypography, LyfeTooltip } from "@lyfegen-platform/react-common";
import { firstNonEmpty, isEmptyValue, sortBySelectors } from "@lyfegen-platform/utils";
import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, IconButtonProps, Typography, withStyles } from "@material-ui/core";
import { Add as AddIcon, Edit as EditIcon, FileCopy as CopyIcon, ExpandMore, Visibility } from "@material-ui/icons";
import { max } from "date-fns";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import ReactTimeAgo from "react-time-ago";
import { useRecoilValue } from "recoil";
import DraftIcon from "../../../components/icons/FormStatus/DraftIcon";
import DueIcon from "../../../components/icons/FormStatus/DueIcon";
import OverdueIcon from "../../../components/icons/FormStatus/OverdueIcon";
import SubmittedIcon from "../../../components/icons/FormStatus/SubmittedIcon";
import LyfegenLogo from "../../../components/icons/LyfegenLogo";
import LyfeAvatar from "../../../components/LyfeAvatar";
import MappedComponent from "../../../components/mapped-components/MappedComponent";
import { EMPTY_VALUE_PLACEHOLDER } from "../../../models/common/data";
import { ModulesEnum } from "../../../models/common/modules";
import { fieldControllerFactory } from "../../../models/flexible/field-controller-factory";
import { FlexEntitySchema } from "../../../models/flexible/flex-entity-schema";
import { FlexFieldController } from "../../../models/flexible/flex-field-controller";
import { CatalogueAttribute, FlexFormStatus, Maybe, OccurrenceEnum, TimeframeRangeType, User } from "../../../models/generated/graphql";
import { coreConfig } from "../../../store/core-config";
import { userPersonalInfoState } from "../../../store/user";
import { formatDataPoint } from "../../../utils/data-point-formatter";
import { flattenFlexEntitySection } from "../../../utils/flatten-flex-entity-data";
import { localeDate } from "../../../utils/locale-date";
import { TrFun } from "../../../utils/types";
import DeleteForm from "../../data/forms/DeleteForm";
import { GetCaseByIdQuery } from "../types/queries.generated";
import useStyles from "../use-styles";
import { DataParsedFlexEntity } from "./Overview";

export type CaseFormStatus = "submitted" | "due" | "overdue" | undefined | null;

export interface IFormsCard {
	entity?: GetCaseByIdQuery["cases"][number]["flexEntitiesConnection"]["edges"][number]["node"];
	caseId: string;
	occurrence: Maybe<OccurrenceEnum>;
	addedBy: Maybe<Pick<User, "id" | "email" | "firstName" | "lastName" | "isDeleted">>;
	isAddedByEnabledInTable: Maybe<boolean>;
	patientAlias?: Maybe<string>;
	formId?: Maybe<string>;
	typeId: string;
	title?: Maybe<string>;
	timeLimit?: Maybe<Date>;
	isTimeLimitPassed?: Maybe<boolean>;
	isEditingFeature: boolean;
	submissionDate?: Maybe<Date>;
	lastUpdatedOn: Maybe<Date>;
	status?: Maybe<FlexFormStatus>;
	timeframeRelation?: TimeframeRangeType;
	index?: number;
	nextForms?: Array<Array<IFormsCard>>;
	isFormOwner?: boolean;
	isCreatable?: boolean;
	isDeletionEnabled?: boolean;
	casesCount?: number;
	isStartingPoint?: boolean;
	caseNonStartingPointForms?: Array<GetCaseByIdQuery["cases"][number]["flexEntitiesConnection"]["edges"][number]>;
	caseFormStatus?: CaseFormStatus;
}

interface ICollapsibleFormsCard {
	forms: Array<IFormsCard>;
	isFormOwner?: boolean;
	isFormCreatable?: boolean | null;
}

type EnhancedAttribute = CatalogueAttribute & {
	identifiers: Array<string>;
	position: number;
	isStartDate?: boolean;
	isEndDate?: boolean;
};

interface ActionButtonProps extends IconButtonProps {
	tooltipTitle: string;
	icon: React.ReactNode;
	id?: string;
}

const copyFormTooltipKey = "copy-form-tooltip-key";

const FormsAccordion = withStyles((theme) => ({
	root: {
		margin: theme.spacing(2),
		marginInline: theme.spacing(0),
		"& .MuiAccordionSummary-root": {
			flexDirection: "row-reverse",
		},
		"& .MuiAccordionSummary-content.Mui-expanded": {
			margin: 0,
		},
		"& .MuiAccordionDetails-root": {
			display: "block",
			backgroundColor: theme.palette.inputTableDivider.main,
			borderRadius: "0 0 4px 4px",
		},
		"&.Mui-expanded": {
			margin: theme.spacing(2),
			marginInline: theme.spacing(0),
		},
		"&:before": {
			content: "none",
		},
	},
	rounded: {
		borderRadius: "4px",
		"&:last-child": {
			borderRadius: "4px",
		},
	},
	expanded: {
		height: "auto",
	},
}))(Accordion);

function getTimeFrameProcessedCustomColumns(attributes: Array<EnhancedAttribute>) {
	return attributes
		.map((attribute) => {
			if (
				[AttributeIdentifiers.CoverageStartDate, AttributeIdentifiers.TherapyTrialStartDate].includes(
					attribute.identifier as AttributeIdentifiers
				)
			) {
				return {
					...attribute,
					caption: "start-date",
					isStartDate: true,
					identifiers: [AttributeIdentifiers.CoverageStartDate, AttributeIdentifiers.TherapyStartDate],
				};
			}

			if (
				[AttributeIdentifiers.CoverageEndDate, AttributeIdentifiers.TherapyTrialEndDate].includes(
					attribute.identifier as AttributeIdentifiers
				)
			) {
				return {
					...attribute,
					caption: "end-date",
					isEndDate: true,
					identifiers: [AttributeIdentifiers.CoverageEndDate, AttributeIdentifiers.TherapyTrialEndDate],
				};
			}

			return attribute;
		})
		.reduce((previous, attribute: EnhancedAttribute) => {
			if (previous.map((att) => att && att.isStartDate).filter(Boolean).length && attribute.isStartDate) {
				return previous;
			}
			if (previous.map((att) => att && att.isEndDate).filter(Boolean).length && attribute.isEndDate) {
				return previous;
			}

			return previous.concat(attribute);
		}, [] as Array<EnhancedAttribute>);
}

function getCaseDetailsFieldsValue(
	entity: DataParsedFlexEntity,
	attribute: EnhancedAttribute,
	fieldControllers: FlexFieldController[],
	t: TrFun
): string {
	const schema = JSON.parse(entity.type.data) as FlexEntitySchema;
	const flattenedData = flattenFlexEntitySection(JSON.parse(entity.data), schema);

	if (attribute.identifiers.length > 1 && attribute.isStartDate) {
		if (flattenedData[AttributeIdentifiers.CoverageStartDate]) {
			const coverageStartController = fieldControllers
				.find((c) => c.path === AttributeIdentifiers.CoverageStartDate)!
				.getValueTypeProps();

			return formatDataPoint(flattenedData[AttributeIdentifiers.CoverageStartDate], coverageStartController, attribute.dataType, t);
		}
		if (flattenedData[AttributeIdentifiers.TherapyTrialStartDate]) {
			const therapyTrialStartController = fieldControllers
				.find((c) => c.path === AttributeIdentifiers.TherapyTrialStartDate)!
				.getValueTypeProps();

			return formatDataPoint(
				flattenedData[AttributeIdentifiers.TherapyTrialStartDate],
				therapyTrialStartController,
				attribute.dataType,
				t
			);
		}
	}

	if (attribute.identifiers.length > 1 && attribute.isEndDate) {
		if (flattenedData[AttributeIdentifiers.CoverageEndDate]) {
			const coverageEndController = fieldControllers
				.find((c) => c.path === AttributeIdentifiers.CoverageEndDate)!
				.getValueTypeProps();

			return formatDataPoint(flattenedData[AttributeIdentifiers.CoverageEndDate], coverageEndController, attribute.dataType, t);
		}
		if (flattenedData[AttributeIdentifiers.TherapyTrialEndDate]) {
			const therapyTrialEndController = fieldControllers
				.find((c) => c.path === AttributeIdentifiers.TherapyTrialEndDate)!
				.getValueTypeProps();

			return formatDataPoint(
				flattenedData[AttributeIdentifiers.TherapyTrialEndDate],
				therapyTrialEndController,
				attribute.dataType,
				t
			);
		}
	}

	const controller = fieldControllers.find((c) => c.path === attribute.identifier)!.getValueTypeProps();

	return formatDataPoint(flattenedData[attribute.identifier], controller, attribute.dataType, t);
}

const ActionButton = ({ tooltipTitle, icon, id, ...iconButtonProps }: ActionButtonProps) => {
	return (
		<LyfeTooltip title={tooltipTitle}>
			<IconButton id={id} {...iconButtonProps}>
				{icon}
			</IconButton>
		</LyfeTooltip>
	);
};

const CollapsibleFormCard = ({ forms, isFormOwner, isFormCreatable }: ICollapsibleFormsCard) => {
	const { fullWidth, divider, miniTableCA } = useStyles();
	const { t } = useTranslation();
	const maxNoOfCustomColumns = 5;
	const { push } = useHistory();
	const draftForms = forms.filter((f) => f.status === FlexFormStatus.Draft);
	const { preferredLanguage, modules } = useRecoilValue(userPersonalInfoState);
	const coreConfigData = useRecoilValue(coreConfig);
	const hasAccessToDataModule = modules.some((module) => module.code === ModulesEnum.data);
	const [isButtonDisabled, setIsButtonDisabled] = useState(false);

	function renderStatusIcon() {
		if (forms.every((f) => f.caseFormStatus === "submitted" && f.status === FlexFormStatus.Submitted)) return <SubmittedIcon />;
		if (forms.some((f) => f.status === FlexFormStatus.Draft)) return <DraftIcon />;

		return forms.some((f) => f.caseFormStatus === "overdue") ? <OverdueIcon /> : <DueIcon />;
	}

	const canShowAddedBy = forms.some((f) => f.addedBy && f.isAddedByEnabledInTable && f.isFormOwner);
	const schema = JSON.parse(forms[0].entity?.type.data as string) as FlexEntitySchema;
	const fieldControllers = schema.sections.flatMap((section) =>
		section.fields.map((field) => fieldControllerFactory(field, section, field.attribute.identifier))
	);
	const customTableColumns = schema.sections
		.flatMap((section) =>
			section.fields.map(
				(field) =>
					({
						...field.attribute,
						position: parseInt(`${section.position}${field.row}${field.position}`),
						identifiers: [field.attribute.identifier],
					} as EnhancedAttribute)
			)
		)
		.filter(
			(field) =>
				forms[0].entity?.type.catalogueAttributesConnection.edges.find((edge) => edge.node.id === field.id)
					?.isDisplayedInCaseTable &&
				forms.some((form) => {
					const flattenedValue = form.entity?.data
						? flattenFlexEntitySection(JSON.parse(form.entity.data), schema)[field.identifier]
						: undefined;

					return Array.isArray(flattenedValue) ? flattenedValue.length !== 0 : !isEmptyValue(flattenedValue);
				})
		)
		.sort(sortBySelectors((field) => field.position));

	const processedCustomTableColumns =
		forms[0].entity &&
		coreConfigData.coverageApprovalTypeIds.includes(forms[0].entity.type.id) &&
		(customTableColumns.map((attribute) => attribute.identifier).includes(AttributeIdentifiers.CoverageStartDate) ||
			customTableColumns.map((attribute) => attribute.identifier).includes(AttributeIdentifiers.TherapyTrialStartDate))
			? getTimeFrameProcessedCustomColumns(customTableColumns)
			: customTableColumns;

	function handleNewFormClick(type: string) {
		push(`/data/forms/new/${type}?caseId=${forms[0].caseId}`);
	}

	function handleEditSubmitted(id: Maybe<string>) {
		push(`/data/forms/${id}?startEdit`);
	}

	function handleCopy(id: string) {
		push(`/data/forms/${id}?copy`);
	}

	function renderActionIcon(status?: Maybe<FlexFormStatus>) {
		switch (status) {
			case FlexFormStatus.Submitted:
				return <Visibility fontSize="small" />;
			default:
				return <EditIcon fontSize="small" />;
		}
	}

	function handleViewClick(id: string) {
		push(`/data/forms/${id}`);
	}

	return (
		<FormsAccordion TransitionProps={{ unmountOnExit: true }}>
			<AccordionSummary expandIcon={<ExpandMore />}>
				<Box display="flex" justifyContent="space-between" alignItems="center" className={fullWidth}>
					<Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
						<Box width="25%" pl={7}>
							<Typography color="textPrimary" style={{ fontWeight: 600, textTransform: "uppercase" }}>
								{t(forms[0].title || "")}
							</Typography>
						</Box>
						<Box width="15%">
							<Typography color="textPrimary">
								{forms.length} {forms.length === 1 ? t("forms-singular").toLowerCase() : t("forms").toLowerCase()}{" "}
								{draftForms.length
									? `| ${draftForms.length} ${draftForms.length === 1 ? t("DRAFT") : t("drafts")}`
									: undefined}
							</Typography>
						</Box>
						<Box width="25%">
							<Typography color="textPrimary">
								{t("last-updated")}{" "}
								<ReactTimeAgo date={max(forms.map((f) => f.lastUpdatedOn || new Date()))} locale={preferredLanguage} />
							</Typography>
						</Box>
						<Box p={1} marginLeft="auto">
							{renderStatusIcon()}
						</Box>
						{hasAccessToDataModule && isFormOwner && (forms[0].formId || forms[0].isCreatable) && isFormCreatable && (
							<Box pr={1} display="flex">
								<Box ml={2}>
									<IconButton
										style={{ padding: 5 }}
										id={`addForm-${forms[0].typeId}`}
										onClick={() => handleNewFormClick(forms[0].typeId)}
										color="primary"
									>
										<AddIcon color="primary" />
									</IconButton>
								</Box>
							</Box>
						)}
					</Box>
				</Box>
			</AccordionSummary>
			<AccordionDetails>
				<Box style={{ marginLeft: "66px" }}>
					<table className={miniTableCA}>
						<thead>
							<tr>
								<th style={{ textAlign: "left" }}>{t("formId")}</th>
								{processedCustomTableColumns?.map((c, ci) =>
									ci < maxNoOfCustomColumns ? (
										<th key={ci}>
											<EllipsisTypography
												styles={{ fontWeight: 700 }}
												text={t(c.shortLabelLocalizationKey)}
												truncate
												variant="body2"
											/>
										</th>
									) : undefined
								)}
								<th style={{ textAlign: "right" }}>{t("submissionDate")}</th>
								<th style={{ textAlign: "left" }}>{t("status")}</th>
								{canShowAddedBy && <th style={{ textAlign: "left" }}>{t("added-by")}</th>}
							</tr>
						</thead>
						<tbody>
							{forms.map((form, i) => (
								<Fragment key={i}>
									<tr>
										<td>{form.formId}</td>
										{processedCustomTableColumns?.map((column, ci) =>
											ci < maxNoOfCustomColumns ? (
												<td key={ci} style={{ textAlign: "center" }}>
													{firstNonEmpty(
														getCaseDetailsFieldsValue(
															form.entity! as DataParsedFlexEntity,
															column,
															fieldControllers,
															t
														),
														EMPTY_VALUE_PLACEHOLDER
													)}
												</td>
											) : undefined
										)}
										<td style={{ textAlign: "right" }}>{localeDate(form.submissionDate)}</td>
										<td>
											{form.caseFormStatus !== undefined && (
												<MappedComponent component="FormsStatusComponent" props={{ ...form }} />
											)}
										</td>
										{canShowAddedBy && (
											<td style={{ textAlign: "left" }}>
												<LyfeAvatar
													toolTipTitle={form.addedBy?.isDeleted ? t("deleted-user") : ""}
													isSystemAvatar={form.occurrence === OccurrenceEnum.AutoGenerated}
													userEmail={form.addedBy?.email}
													firstName={
														form?.occurrence === OccurrenceEnum.AutoGenerated
															? t("system-generated")
															: form.addedBy?.firstName || ""
													}
													lastName={
														form?.occurrence === OccurrenceEnum.AutoGenerated
															? ""
															: form.addedBy?.lastName || ""
													}
													size="small"
													initialsProps={{ style: { fontSize: "14px" } }}
												>
													{form.occurrence === OccurrenceEnum.AutoGenerated ? <LyfegenLogo /> : undefined}
												</LyfeAvatar>
											</td>
										)}
										{hasAccessToDataModule && isFormOwner ? (
											<td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
												<ActionButton
													tooltipTitle={t("view-form-tooltip")}
													onClick={() => handleViewClick(form.formId!)}
													color="primary"
													disabled={isButtonDisabled}
													icon={renderActionIcon(form.status)}
												/>
												<ActionButton
													tooltipTitle={t(copyFormTooltipKey)}
													onClick={() => handleCopy(form.formId!)}
													color="primary"
													disabled={isButtonDisabled}
													icon={<CopyIcon fontSize="small" />}
													id={`copyForm-${form.typeId}`}
												/>
												{!!form.submissionDate && form.isEditingFeature && (
													<ActionButton
														tooltipTitle={t("edit-form-tooltip")}
														onClick={() => handleEditSubmitted(form.formId!)}
														color="primary"
														disabled={isButtonDisabled}
														icon={<EditIcon fontSize="small" />}
														id={`editForm-${form.typeId}`}
													/>
												)}
												{form.isDeletionEnabled && (
													<DeleteForm
														id={form.formId!}
														caseId={form.caseId}
														isIcon={true}
														caseCount={form.casesCount}
														isStartingPoint={form.isStartingPoint}
														caseNonStartingPointForms={form.caseNonStartingPointForms}
														setIsButtonDisabled={setIsButtonDisabled}
														isButtonDisabled={isButtonDisabled}
													/>
												)}
											</td>
										) : undefined}
									</tr>
									{!(forms.length - 1 === i) && <tr className={divider}></tr>}
								</Fragment>
							))}
						</tbody>
					</table>
				</Box>
			</AccordionDetails>
		</FormsAccordion>
	);
};

export default CollapsibleFormCard;
